import React, { useEffect, useMemo, useState, useCallback } from "react";
// Chart components are now resolved via the registry for extensibility
import { getChartComponent } from "./widgets/registry";
import { getDashboardChartFiltered } from "api/dashboard";
import { getJSONObject } from "utils/helper";
import { normalizePlotConfig } from "utils/chat";
import dayjs from "dayjs";
import ChartMeta from "./widgets/Meta/ChartMeta";
import ChartSideMeta from "./widgets/Meta/ChartSideMeta";
import ChartInlineMeta from "./widgets/Meta/ChartInlineMeta";
import { useDispatch, useSelector } from "react-redux";
import { fetchInitialDataThunk } from "slices/initialData/thunk";
import { formatters } from "components/DashboardNew/config";
import SectionFilters from "./Filters/SectionFilters";
import { getValueByPath, formatValue } from "./widgets/Meta/metaUtils";
import ChartUpIcon from "../../../src/images/dashboard/chart-up.svg";
import ChartDownIcon from "../../../src/images/dashboard/chart-down.svg";

// Inline meta icons are handled inside ChartInlineMeta via an internal icon map

// Placeholder for future types (chart, table, etc.)
const UnknownCard = ({ title }) => {
  return (
    <div className="dashboard-new-card">
      {title ? <h3>{title}</h3> : null}
      <div>Unsupported section type</div>
    </div>
  );
};

export default function ChartRenderer({ section }) {
  // Use safe defaults to avoid conditional hooks
  const safeSection = section || {};
  const {
    type,
    title,
    subtitle = "",
    props = {},
    initial,
    theme: sectionTheme = {},
  } = safeSection;
  const Chart = getChartComponent(type) || UnknownCard;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [plotConfig, setPlotConfig] = useState(null);
  const [apiRow, setApiRow] = useState(null);

  // HEADER DROP-OFF 
  const headerExtra = safeSection?.meta?.headerExtraDelta;

  const {
    showHeaderExtra,
    headerExtraValue,
    headerExtraPositive,
    headerExtraLabel,
    headerExtraColor,
  } = useMemo(() => {
    if (!headerExtra || headerExtra.visible === false || !apiRow) {
      return {
        showHeaderExtra: false,
        headerExtraValue: "",
        headerExtraPositive: false,
        headerExtraLabel: "",
        headerExtraColor: "#DC2626",
      };
    }

    const raw = headerExtra.path
      ? getValueByPath({ apiRow }, headerExtra.path)
      : headerExtra.default;

    const num = Number(raw);
    if (Number.isNaN(num)) {
      return {
        showHeaderExtra: false,
        headerExtraValue: "",
        headerExtraPositive: false,
        headerExtraLabel: "",
        headerExtraColor: "#DC2626",
      };
    }

    return {
      showHeaderExtra: true,
      headerExtraValue: formatValue(num, headerExtra.format || "percent"),
      headerExtraPositive: headerExtra.invert ? num <= 0 : num >= 0,
      headerExtraLabel: headerExtra.label || "(Drop-off rate)",
      headerExtraColor: headerExtra.color || "#DC2626",
    };
  }, [headerExtra, apiRow]);

  const [legendItems, setLegendItems] = useState([]);
  const [selectedData, setSelectedData] = useState(null);

  const areLegendItemsEqual = useCallback((a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      const ai = a[i] || {};
      const bi = b[i] || {};
      if (ai.label !== bi.label || ai.color !== bi.color) return false;
    }
    return true;
  }, []);

  const handleExposeLegend = useCallback(
    (items) => {
      if (!Array.isArray(items)) return;
      setLegendItems((prev) =>
        areLegendItemsEqual(prev, items) ? prev : items
      );
    },
    [areLegendItemsEqual]
  );
  const [isFilterable, setIsFilterable] = useState(false);
  const [range, setRange] = useState([null, null]);
  const [startDate, endDate] = range;
  const [hasFilteredOnce, setHasFilteredOnce] = useState(false);

  const dispatch = useDispatch();
  const key =
    initial?.dashboard_id && initial?.chartid
      ? `${initial.dashboard_id}:${initial.chartid}`
      : null;
  const cached = useSelector((state) =>
    key ? state.InitialDataReducer?.byKey?.[key] : null
  );
  const cachedLoading = useSelector((state) =>
    key ? state.InitialDataReducer?.loadingByKey?.[key] : false
  );
  const cachedError = useSelector((state) =>
    key ? state.InitialDataReducer?.errorByKey?.[key] : null
  );

  // Fetch initial data once per unique key and hydrate local state from cache
  useEffect(() => {
    if (!key) {
      setLoading(false);
      return;
    }
    if (!cached && !cachedLoading) {
      dispatch(
        fetchInitialDataThunk({
          dashboard_id: initial.dashboard_id,
          chartid: initial.chartid,
        })
      );
    }
    setLoading(Boolean(cachedLoading));
    if (cached) {
      setRows(Array.isArray(cached.rows) ? cached.rows : []);
      setPlotConfig(cached.plotConfig || null);
      setApiRow(cached.apiRow || null);
      setIsFilterable(Boolean(cached.isFilterable));
    }
    if (cachedError) {
      setError("Failed to load chart");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, cached, cachedLoading, cachedError]);

  // Fetch filtered data when the date range is changed
  useEffect(() => {
    const shouldFilter = Boolean(
      isFilterable &&
        startDate &&
        endDate &&
        initial?.chartid &&
        initial?.dashboard_id
    );
    if (!shouldFilter) return;
    let isMounted = true;
    const fetchFiltered = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await getDashboardChartFiltered({
          chartid: initial.chartid,
          dashboard_id: initial.dashboard_id,
          start_date: dayjs(startDate).format("YYYY-MM-DD"),
          end_date: dayjs(endDate).format("YYYY-MM-DD"),
        });
        const r = Array.isArray(resp?.[0])
          ? resp[0]
          : Array.isArray(resp)
          ? resp
          : [];
        const row = r?.[0] ?? null;
        if (!isMounted) return;
        const df =
          getJSONObject(
            row?.updated_df_data ? row.updated_df_data : row?.df_data
          ) || [];
        const hc = getJSONObject(row?.highchart_code) || {};

        setRows(Array.isArray(df) ? df : []);
        setPlotConfig(hc);
        setApiRow(row || null);
        setHasFilteredOnce(true);
      } catch (err) {
        if (!isMounted) return;
        setError("Failed to apply filter");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchFiltered();
    return () => {
      isMounted = false;
    };
  }, [
    isFilterable,
    startDate,
    endDate,
    initial?.chartid,
    initial?.dashboard_id,
  ]);

  // When the range is cleared after a filtered view, restore cached initial data (no refetch)
  useEffect(() => {
    const shouldReloadInitial = Boolean(
      isFilterable &&
        hasFilteredOnce &&
        !startDate &&
        !endDate &&
        initial?.chartid &&
        initial?.dashboard_id
    );
    if (!shouldReloadInitial) return;
    if (cached) {
      setRows(Array.isArray(cached.rows) ? cached.rows : []);
      setPlotConfig(cached.plotConfig || null);
      setApiRow(cached.apiRow || null);
      setHasFilteredOnce(false);
      setLoading(false);
    } else if (!cachedLoading && key) {
      dispatch(
        fetchInitialDataThunk({
          dashboard_id: initial.dashboard_id,
          chartid: initial.chartid,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isFilterable,
    hasFilteredOnce,
    startDate,
    endDate,
    key,
    cached,
    cachedLoading,
  ]);

  // Apply generic skip filtering across all charts (like ProgressStatCard)
  const displayRows = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    const skipConfig = props?.items?.skip;
    if (
      !skipConfig?.key ||
      !Array.isArray(skipConfig?.values) ||
      skipConfig.values.length === 0
    ) {
      return rows;
    }
    return rows.filter(
      (row) => !skipConfig.values.includes(row?.[skipConfig.key])
    );
  }, [rows, props?.items?.skip]);

  const normalized = useMemo(
    () =>
      normalizePlotConfig(plotConfig, displayRows, title || "Report", "column"),
    [plotConfig, displayRows, title]
  );
  // Allow theme overrides for axis keys
  const effectiveXKey = sectionTheme?.xAxisKey || normalized.xAxisKey;
  const effectiveYKeys = useMemo(() => {
    if (
      Array.isArray(sectionTheme?.yAxisKeys) &&
      sectionTheme.yAxisKeys.length > 0
    )
      return sectionTheme.yAxisKeys;
    if (sectionTheme?.yAxisKey) return [sectionTheme.yAxisKey];
    return Array.isArray(normalized.yAxisKeys) ? normalized.yAxisKeys : [];
  }, [sectionTheme?.yAxisKeys, sectionTheme?.yAxisKey, normalized.yAxisKeys]);
  const yKey = Array.isArray(effectiveYKeys) ? effectiveYKeys[0] : undefined;

  const chartData = useMemo(() => {
    if (!Array.isArray(displayRows) || !effectiveXKey || !yKey) return [];
    return displayRows.map((row) => ({
      name: row?.[effectiveXKey],
      value: row?.[yKey],
    }));
  }, [displayRows, effectiveXKey, yKey]);

  // Compute highlight selection from theme.selection and inject highlightX/highlightIndex for all charts
  const enhancedTheme = useMemo(() => {
    const theme = sectionTheme || {};
    // If explicit highlight provided, respect it
    if (
      typeof theme.highlightIndex === "number" ||
      typeof theme.highlightX !== "undefined"
    ) {
      return theme;
    }

    const selection = theme?.selection || null;
    if (!selection) return theme;

    // Prepare formatted names (categories) and values
    const rawNames =
      Array.isArray(displayRows) && effectiveXKey
        ? displayRows.map((r) => r?.[effectiveXKey])
        : [];
    const values =
      Array.isArray(displayRows) && yKey
        ? displayRows.map((r) => r?.[yKey])
        : [];

    const applyXFormatter = (label) => {
      // Allow selection.labelFormatter to override chart xAxisKeyFormatter
      const formatterKey =
        selection?.labelFormatter || theme?.xAxisKeyFormatter;
      if (
        typeof formatterKey === "string" &&
        typeof formatters?.[formatterKey] === "function"
      ) {
        try {
          return formatters[formatterKey](label);
        } catch (_e) {}
      }
      return label;
    };
    const names = rawNames.map((n) => applyXFormatter(n));

    const toNumber = (v) => {
      const num = typeof v === "number" ? v : Number(v);
      return Number.isFinite(num) ? num : NaN;
    };

    const findExtremeIndex = (arr, mode) => {
      if (!Array.isArray(arr) || arr.length === 0) return -1;
      let idx = -1;
      let best = mode === "min" ? Infinity : -Infinity;
      arr.forEach((v, i) => {
        const num = toNumber(v);
        if (!Number.isFinite(num)) return;
        if ((mode === "min" && num < best) || (mode === "max" && num > best)) {
          best = num;
          idx = i;
        }
      });
      return idx;
    };

    const mode = selection?.mode;
    let highlightIndex;
    let highlightX;

    if (mode === "extreme") {
      const axis = selection?.axis === "x" ? "x" : "y";
      const which = selection?.type === "min" ? "min" : "max";
      if (axis === "y") {
        highlightIndex = findExtremeIndex(values, which);
      } else {
        // Extreme on x only makes sense if x is numeric
        const xNums = names.map(toNumber);
        highlightIndex = findExtremeIndex(xNums, which);
      }
    } else if (mode === "today") {
      const unit = selection?.unit || "weekday";
      let label;
      if (unit === "weekday")
        label = dayjs().format(selection?.format || "ddd");
      else if (unit === "month")
        label = dayjs().format(selection?.format || "MMM");
      else if (unit === "date")
        label = dayjs().format(selection?.format || "D");
      else if (unit === "year")
        label = dayjs().format(selection?.format || "YYYY");
      else label = dayjs().format(selection?.format || "ddd");
      if (
        selection?.map &&
        typeof selection.map === "object" &&
        selection.map !== null
      ) {
        label = selection.map[label] ?? label;
      }
      label = applyXFormatter(label);
      const idx = names.findIndex(
        (n) => String(n).toLowerCase() === String(label).toLowerCase()
      );
      if (idx >= 0) {
        highlightIndex = idx;
        highlightX = names[idx];
      }
    } else if (mode === "position") {
      if (typeof selection?.index === "number") {
        highlightIndex = selection.index;
      } else {
        const from = selection?.from === "last" ? "last" : "first";
        const nth = typeof selection?.nth === "number" ? selection.nth : 1;
        const len = names.length;
        if (from === "last") highlightIndex = Math.max(0, len - nth);
        else highlightIndex = Math.max(0, nth - 1);
      }
    } else if (mode === "xMatch") {
      const target = applyXFormatter(selection?.x);
      const idx = names.findIndex(
        (n) => String(n).toLowerCase() === String(target).toLowerCase()
      );
      if (idx >= 0) {
        highlightIndex = idx;
        highlightX = names[idx];
      }
    }

    if (typeof highlightIndex === "number" && highlightIndex >= 0) {
      highlightX =
        typeof highlightX !== "undefined"
          ? highlightX
          : names?.[highlightIndex];
      return { ...theme, highlightIndex, highlightX };
    }
    return theme;
  }, [sectionTheme, displayRows, effectiveXKey, yKey]);

  const handleDataPointClick = useCallback(
    (payload) => {
      const enriched = {
        ...(payload || {}),
        chartId: safeSection?.id,
        chartType: type,
        title,
      };
      setSelectedData(enriched);
      try {
        if (
          typeof window !== "undefined" &&
          typeof window.dispatchEvent === "function"
        ) {
          window.dispatchEvent(
            new CustomEvent("chart:dataPointSelected", { detail: enriched })
          );
        }
      } catch (_e) {}
    },
    [safeSection?.id, type, title]
  );

  const [filters, setFilters] = useState({});

  const handleFilterUpdate = useCallback((selectedFilter, filterOption) => {
    if (filterOption?.type === "basic" && filterOption?.param?.[0]) {
      setFilters((prev) => ({
        ...prev,
        [filterOption?.param[0]]: selectedFilter?.value,
      }));
    }
  }, []);

  return (
    <div className="dashboard-new-card">
      {/* Show the Title and Filters */}
      <div className="flex items-center justify-between">
        {/* Chart Title */}
        <div className="flex flex-col justify-start gap-1">
          <h3 className={`text-base font-medium`}>{title}</h3>
          <p className={`text-sm font-normal`}>{subtitle}</p>
        </div>

        {/* Date Range Filter */}

        <div className="flex items-center gap-3">
          {/* HEADER DROP-OFF */}
          {showHeaderExtra ? (
            <div className="flex flex-col items-center gap-1">
              <div
                className={`flex items-center gap-1 px-2 py-1 rounded-md text-sm font-medium ${
                  headerExtraPositive
                    ? "bg-green-100 text-green-700"
                    : "bg-[#FEF2F0] text-[#E62400]"
                }`}
              >
                <span>
                  {headerExtraPositive ? "+" : "-"}
                  {headerExtraValue}
                </span>

                <img
                  src={headerExtraPositive ? ChartUpIcon : ChartDownIcon}
                  alt=""
                  className="h-3 w-3"
                />
              </div>

              {headerExtraLabel ? (
                <span className="text-xs" style={{ color: headerExtraColor }}>
                  {headerExtraLabel}
                </span>
              ) : null}
            </div>
          ) : null}

          {/* Date Range */}
          <SectionFilters
            filtersConfig={safeSection?.filters}
            isFilterable={isFilterable}
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={setRange}
            onBasicFilterChange={handleFilterUpdate}
            variant="header"
          />
        </div>
      </div>

      {/* Show the error if it exists */}
      {error ? (
        <div className="mt-2 text-sm" style={{ color: "#ef4444" }}>
          {error}
        </div>
      ) : null}

      {/* Optional meta section (top) driven by layout config and API data */}
      {props?.meta || safeSection?.meta ? (
        <ChartMeta
          meta={props?.meta || safeSection?.meta}
          dataContext={{
            apiRow,
            rows: displayRows,
            normalized,
            plotConfig,
            legendItems,
            selectedData,
          }}
          chartType={type}
          theme={sectionTheme}
          rightExtras={
            <SectionFilters
              filtersConfig={safeSection?.filters}
              isFilterable={isFilterable}
              startDate={startDate}
              endDate={endDate}
              onDateRangeChange={setRange}
              onBasicFilterChange={handleFilterUpdate}
              variant="meta"
            />
          }
        />
      ) : null}

      {/* Inline meta row (below ChartMeta), driven by layout config */}
      {props?.inlineMeta || safeSection?.inlineMeta ? (
        <ChartInlineMeta
          inlineMeta={props?.inlineMeta || safeSection?.inlineMeta}
          dataContext={{
            apiRow,
            rows: displayRows,
            normalized,
            plotConfig,
            legendItems,
            selectedData,
          }}
        />
      ) : null}

      <div
        className={`flex items-end justify-between gap-2 mt-2 overflow-hidden`}
      >
        {/* Optional side meta (left) list of items */}
        {props?.sideMeta || safeSection?.sideMeta ? (
          <div className="w-1/2 overflow-hidden">
            <ChartSideMeta
              sideMeta={props?.sideMeta || safeSection?.sideMeta}
              dataContext={{
                apiRow,
                rows: displayRows,
                normalized,
                plotConfig,
                legendItems,
                selectedData,
              }}
            />
          </div>
        ) : null}

        {/* Render Point 2: This will render value and delta but both should be independent to render */}
        {/* <div className={`flex flex-col justify-end gap-3 mt-auto`}>
                    <h3 className="mb-0 font-semibold text-2xl">$400</h3>
                    <div className={`inline-flex flex-nowrap gap-1 items-center rounded-md py-[6px] px-2 bg-green-100`}>
                        <span className={`text-base font-medium text-green-600 text-nowrap`}>+ 10%</span>
                        <img src={ChartUpIcon} alt="" className="h-3 w-3" />
                    </div>
                </div> */}

        <div
          className={`relative mt-2 flex-shrink ${
            props?.sideMeta || safeSection?.sideMeta ? "w-1/2" : "w-full"
          }`}
          aria-busy={loading ? "true" : "false"}
        >
          <div className="min-h-[280px]">
            {type !== "bar-chart" ? (
              <Chart
                rows={displayRows}
                xKey={effectiveXKey}
                yAxisKeys={Array.isArray(effectiveYKeys) ? effectiveYKeys : []}
                theme={enhancedTheme}
                dataContext={{
                  apiRow,
                  rows: displayRows,
                  normalized,
                  plotConfig,
                }}
                onExposeLegend={handleExposeLegend}
                onDataPointClick={handleDataPointClick}
              />
            ) : (
              <Chart
                data={chartData}
                theme={enhancedTheme}
                dataContext={{
                  apiRow,
                  rows: displayRows,
                  normalized,
                  plotConfig,
                }}
                onExposeLegend={handleExposeLegend}
                onDataPointClick={handleDataPointClick}
              />
            )}
          </div>
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white/70">
              <svg
                className="h-6 w-6 animate-spin text-gray-600"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}








// import React, { useEffect, useMemo, useState, useCallback } from 'react';
// // Chart components are now resolved via the registry for extensibility
// import { getChartComponent } from './widgets/registry';
// import { getDashboardChartFiltered } from 'api/dashboard';
// import { getJSONObject } from 'utils/helper';
// import { normalizePlotConfig } from 'utils/chat';
// import dayjs from 'dayjs';
// import ChartMeta from './widgets/Meta/ChartMeta';
// import ChartSideMeta from './widgets/Meta/ChartSideMeta';
// import ChartInlineMeta from './widgets/Meta/ChartInlineMeta';
// import { useDispatch, useSelector } from 'react-redux';
// import { fetchInitialDataThunk } from 'slices/initialData/thunk';
// import { formatters } from 'components/DashboardNew/config';
// import SectionFilters from './Filters/SectionFilters';

// // Inline meta icons are handled inside ChartInlineMeta via an internal icon map

// // Placeholder for future types (chart, table, etc.)
// const UnknownCard = ({ title }) => {
//     return (
//         <div className="dashboard-new-card">
//             {title ? <h3>{title}</h3> : null}
//             <div>Unsupported section type</div>
//         </div>
//     );
// };

// export default function ChartRenderer({ section }) {
//     // Use safe defaults to avoid conditional hooks
//     const safeSection = section || {};
//     const { type, title, subtitle = "", props = {}, initial, theme: sectionTheme = {} } = safeSection;
//     const Chart = getChartComponent(type) || UnknownCard;

//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);
//     const [rows, setRows] = useState([]);
//     const [plotConfig, setPlotConfig] = useState(null);
//     const [apiRow, setApiRow] = useState(null);
//     const [legendItems, setLegendItems] = useState([]);
//     const [selectedData, setSelectedData] = useState(null);

//     const areLegendItemsEqual = useCallback((a, b) => {
//         if (a === b) return true;
//         if (!Array.isArray(a) || !Array.isArray(b)) return false;
//         if (a.length !== b.length) return false;
//         for (let i = 0; i < a.length; i++) {
//             const ai = a[i] || {};
//             const bi = b[i] || {};
//             if (ai.label !== bi.label || ai.color !== bi.color) return false;
//         }
//         return true;
//     }, []);

//     const handleExposeLegend = useCallback((items) => {
//         if (!Array.isArray(items)) return;
//         setLegendItems(prev => (areLegendItemsEqual(prev, items) ? prev : items));
//     }, [areLegendItemsEqual]);
//     const [isFilterable, setIsFilterable] = useState(false);
//     const [range, setRange] = useState([null, null]);
//     const [startDate, endDate] = range;
//     const [hasFilteredOnce, setHasFilteredOnce] = useState(false);

//     const dispatch = useDispatch();
//     const key = initial?.dashboard_id && initial?.chartid ? `${initial.dashboard_id}:${initial.chartid}` : null;
//     const cached = useSelector(state => key ? state.InitialDataReducer?.byKey?.[key] : null);
//     const cachedLoading = useSelector(state => key ? state.InitialDataReducer?.loadingByKey?.[key] : false);
//     const cachedError = useSelector(state => key ? state.InitialDataReducer?.errorByKey?.[key] : null);

//     // Fetch initial data once per unique key and hydrate local state from cache
//     useEffect(() => {
//         if (!key) {
//             setLoading(false);
//             return;
//         }
//         if (!cached && !cachedLoading) {
//             dispatch(fetchInitialDataThunk({ dashboard_id: initial.dashboard_id, chartid: initial.chartid }));
//         }
//         setLoading(Boolean(cachedLoading));
//         if (cached) {
//             setRows(Array.isArray(cached.rows) ? cached.rows : []);
//             setPlotConfig(cached.plotConfig || null);
//             setApiRow(cached.apiRow || null);
//             setIsFilterable(Boolean(cached.isFilterable));
//         }
//         if (cachedError) {
//             setError('Failed to load chart');
//         }
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, [key, cached, cachedLoading, cachedError]);

//     // Fetch filtered data when the date range is changed
//     useEffect(() => {
//         const shouldFilter = Boolean(isFilterable && startDate && endDate && initial?.chartid && initial?.dashboard_id);
//         if (!shouldFilter) return;
//         let isMounted = true;
//         const fetchFiltered = async () => {
//             setLoading(true);
//             setError(null);
//             try {
//                 const resp = await getDashboardChartFiltered({
//                     chartid: initial.chartid,
//                     dashboard_id: initial.dashboard_id,
//                     start_date: dayjs(startDate).format('YYYY-MM-DD'),
//                     end_date: dayjs(endDate).format('YYYY-MM-DD'),
//                 });
//                 const r = Array.isArray(resp?.[0]) ? resp[0] : Array.isArray(resp) ? resp : [];
//                 const row = r?.[0] ?? null;
//                 if (!isMounted) return;
//                 const df = getJSONObject(row?.updated_df_data ? row.updated_df_data : row?.df_data) || [];
//                 const hc = getJSONObject(row?.highchart_code) || {};

//                 setRows(Array.isArray(df) ? df : []);
//                 setPlotConfig(hc);
//                 setApiRow(row || null);
//                 setHasFilteredOnce(true);
//             } catch (err) {
//                 if (!isMounted) return;
//                 setError('Failed to apply filter');
//             } finally {
//                 if (isMounted) setLoading(false);
//             }
//         };
//         fetchFiltered();
//         return () => { isMounted = false; };
//     }, [isFilterable, startDate, endDate, initial?.chartid, initial?.dashboard_id]);

//     // When the range is cleared after a filtered view, restore cached initial data (no refetch)
//     useEffect(() => {
//         const shouldReloadInitial = Boolean(
//             isFilterable && hasFilteredOnce && !startDate && !endDate && initial?.chartid && initial?.dashboard_id
//         );
//         if (!shouldReloadInitial) return;
//         if (cached) {
//             setRows(Array.isArray(cached.rows) ? cached.rows : []);
//             setPlotConfig(cached.plotConfig || null);
//             setApiRow(cached.apiRow || null);
//             setHasFilteredOnce(false);
//             setLoading(false);
//         } else if (!cachedLoading && key) {
//             dispatch(fetchInitialDataThunk({ dashboard_id: initial.dashboard_id, chartid: initial.chartid }));
//         }
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, [isFilterable, hasFilteredOnce, startDate, endDate, key, cached, cachedLoading]);

//     // Apply generic skip filtering across all charts (like ProgressStatCard)
//     const displayRows = useMemo(() => {
//         if (!Array.isArray(rows)) return [];
//         const skipConfig = props?.items?.skip;
//         if (!skipConfig?.key || !Array.isArray(skipConfig?.values) || skipConfig.values.length === 0) {
//             return rows;
//         }
//         return rows.filter(row => !skipConfig.values.includes(row?.[skipConfig.key]));
//     }, [rows, props?.items?.skip]);

//     const normalized = useMemo(
//         () => normalizePlotConfig(plotConfig, displayRows, title || 'Report', 'column'),
//         [plotConfig, displayRows, title]
//     );
//     // Allow theme overrides for axis keys
//     const effectiveXKey = sectionTheme?.xAxisKey || normalized.xAxisKey;
//     const effectiveYKeys = useMemo(() => {
//         if (Array.isArray(sectionTheme?.yAxisKeys) && sectionTheme.yAxisKeys.length > 0) return sectionTheme.yAxisKeys;
//         if (sectionTheme?.yAxisKey) return [sectionTheme.yAxisKey];
//         return Array.isArray(normalized.yAxisKeys) ? normalized.yAxisKeys : [];
//     }, [sectionTheme?.yAxisKeys, sectionTheme?.yAxisKey, normalized.yAxisKeys]);
//     const yKey = Array.isArray(effectiveYKeys) ? effectiveYKeys[0] : undefined;

//     const chartData = useMemo(() => {
//         if (!Array.isArray(displayRows) || !effectiveXKey || !yKey) return [];
//         return displayRows.map(row => ({ name: row?.[effectiveXKey], value: row?.[yKey] }));
//     }, [displayRows, effectiveXKey, yKey]);

//     // Compute highlight selection from theme.selection and inject highlightX/highlightIndex for all charts
//     const enhancedTheme = useMemo(() => {
//         const theme = sectionTheme || {};
//         // If explicit highlight provided, respect it
//         if (typeof theme.highlightIndex === 'number' || typeof theme.highlightX !== 'undefined') {
//             return theme;
//         }

//         const selection = theme?.selection || null;
//         if (!selection) return theme;

//         // Prepare formatted names (categories) and values
//         const rawNames = Array.isArray(displayRows) && effectiveXKey ? displayRows.map(r => r?.[effectiveXKey]) : [];
//         const values = Array.isArray(displayRows) && yKey ? displayRows.map(r => r?.[yKey]) : [];

//         const applyXFormatter = (label) => {
//             // Allow selection.labelFormatter to override chart xAxisKeyFormatter
//             const formatterKey = selection?.labelFormatter || theme?.xAxisKeyFormatter;
//             if (typeof formatterKey === 'string' && typeof formatters?.[formatterKey] === 'function') {
//                 try { return formatters[formatterKey](label); } catch (_e) { }
//             }
//             return label;
//         };
//         const names = rawNames.map(n => applyXFormatter(n));

//         const toNumber = (v) => {
//             const num = typeof v === 'number' ? v : Number(v);
//             return Number.isFinite(num) ? num : NaN;
//         };

//         const findExtremeIndex = (arr, mode) => {
//             if (!Array.isArray(arr) || arr.length === 0) return -1;
//             let idx = -1; let best = mode === 'min' ? Infinity : -Infinity;
//             arr.forEach((v, i) => {
//                 const num = toNumber(v);
//                 if (!Number.isFinite(num)) return;
//                 if ((mode === 'min' && num < best) || (mode === 'max' && num > best)) {
//                     best = num; idx = i;
//                 }
//             });
//             return idx;
//         };

//         const mode = selection?.mode;
//         let highlightIndex;
//         let highlightX;

//         if (mode === 'extreme') {
//             const axis = selection?.axis === 'x' ? 'x' : 'y';
//             const which = selection?.type === 'min' ? 'min' : 'max';
//             if (axis === 'y') {
//                 highlightIndex = findExtremeIndex(values, which);
//             } else {
//                 // Extreme on x only makes sense if x is numeric
//                 const xNums = names.map(toNumber);
//                 highlightIndex = findExtremeIndex(xNums, which);
//             }
//         } else if (mode === 'today') {
//             const unit = selection?.unit || 'weekday';
//             let label;
//             if (unit === 'weekday') label = dayjs().format(selection?.format || 'ddd');
//             else if (unit === 'month') label = dayjs().format(selection?.format || 'MMM');
//             else if (unit === 'date') label = dayjs().format(selection?.format || 'D');
//             else if (unit === 'year') label = dayjs().format(selection?.format || 'YYYY');
//             else label = dayjs().format(selection?.format || 'ddd');
//             if (selection?.map && typeof selection.map === 'object' && selection.map !== null) {
//                 label = selection.map[label] ?? label;
//             }
//             label = applyXFormatter(label);
//             const idx = names.findIndex(n => String(n).toLowerCase() === String(label).toLowerCase());
//             if (idx >= 0) { highlightIndex = idx; highlightX = names[idx]; }
//         } else if (mode === 'position') {
//             if (typeof selection?.index === 'number') {
//                 highlightIndex = selection.index;
//             } else {
//                 const from = selection?.from === 'last' ? 'last' : 'first';
//                 const nth = typeof selection?.nth === 'number' ? selection.nth : 1;
//                 const len = names.length;
//                 if (from === 'last') highlightIndex = Math.max(0, len - nth);
//                 else highlightIndex = Math.max(0, nth - 1);
//             }
//         } else if (mode === 'xMatch') {
//             const target = applyXFormatter(selection?.x);
//             const idx = names.findIndex(n => String(n).toLowerCase() === String(target).toLowerCase());
//             if (idx >= 0) { highlightIndex = idx; highlightX = names[idx]; }
//         }

//         if (typeof highlightIndex === 'number' && highlightIndex >= 0) {
//             highlightX = typeof highlightX !== 'undefined' ? highlightX : names?.[highlightIndex];
//             return { ...theme, highlightIndex, highlightX };
//         }
//         return theme;
//     }, [sectionTheme, displayRows, effectiveXKey, yKey]);

//     const handleDataPointClick = useCallback((payload) => {
//         const enriched = {
//             ...(payload || {}),
//             chartId: safeSection?.id,
//             chartType: type,
//             title,
//         };
//         setSelectedData(enriched);
//         try {
//             if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
//                 window.dispatchEvent(new CustomEvent('chart:dataPointSelected', { detail: enriched }));
//             }
//         } catch (_e) { }
//     }, [safeSection?.id, type, title]);

//     const [filters, setFilters] = useState({});

//     const handleFilterUpdate = useCallback((selectedFilter, filterOption) => {
//         if (filterOption?.type === "basic" && filterOption?.param?.[0]) {
//             setFilters(prev => ({
//                 ...prev,
//                 [filterOption?.param[0]]: selectedFilter?.value,
//             }));
//         }
//     }, []);

//     return (
//         <div className="dashboard-new-card">
//             {/* Show the Title and Filters */}
//             <div className="flex items-center justify-between">
//                 {/* Chart Title */}
//                 <div className="flex flex-col justify-start gap-1">
//                     <h3 className={`text-base font-medium`}>{title}</h3>
//                     <p className={`text-sm font-normal`}>{subtitle}</p>
//                 </div>

//                 {/* Date Range Filter */}

//                 <div className={`flex items-center gap-2`}>

//                     {/* Header Delta */}
//                     {/* {safeSection?.headerMeta?.delta ? (
//                         <GrowthChip
//                             value={safeSection?.headerMeta?.delta?.value}
//                             isPositive={safeSection?.headerMeta?.delta?.isPositive}
//                             isPercent={safeSection?.headerMeta?.delta?.isPercent}
//                         />
//                     ) : null} */}

//                     <SectionFilters
//                         filtersConfig={safeSection?.filters}
//                         isFilterable={isFilterable}
//                         startDate={startDate}
//                         endDate={endDate}
//                         onDateRangeChange={setRange}
//                         onBasicFilterChange={handleFilterUpdate}
//                         variant="header"
//                     />

//                 </div>
//             </div>

//             {/* Show the error if it exists */}
//             {error ? (
//                 <div className="mt-2 text-sm" style={{ color: '#ef4444' }}>{error}</div>
//             ) : null}

//             {/* Optional meta section (top) driven by layout config and API data */}
//             {(props?.meta || safeSection?.meta) ? (
//                 <ChartMeta
//                     meta={props?.meta || safeSection?.meta}
//                     dataContext={{ apiRow, rows: displayRows, normalized, plotConfig, legendItems, selectedData }}
//                     chartType={type}
//                     theme={sectionTheme}
//                     rightExtras={
//                         <SectionFilters
//                             filtersConfig={safeSection?.filters}
//                             isFilterable={isFilterable}
//                             startDate={startDate}
//                             endDate={endDate}
//                             onDateRangeChange={setRange}
//                             onBasicFilterChange={handleFilterUpdate}
//                             variant="meta"
//                         />
//                     }
//                 />
//             ) : null}

//             {/* Inline meta row (below ChartMeta), driven by layout config */}
//             {(props?.inlineMeta || safeSection?.inlineMeta) ? (
//                 <ChartInlineMeta
//                     inlineMeta={props?.inlineMeta || safeSection?.inlineMeta}
//                     dataContext={{ apiRow, rows: displayRows, normalized, plotConfig, legendItems, selectedData }}
//                 />
//             ) : null}



//             <div className={`flex items-end justify-between gap-2 mt-2 overflow-hidden`}>


//                 {/* Optional side meta (left) list of items */}
//                 {(props?.sideMeta || safeSection?.sideMeta) ? (
//                     <div className="w-1/2 overflow-hidden">
//                         <ChartSideMeta
//                             sideMeta={props?.sideMeta || safeSection?.sideMeta}
//                             dataContext={{ apiRow, rows: displayRows, normalized, plotConfig, legendItems, selectedData }}
//                         />
//                     </div>
//                 ) : null}

//                 {/* Render Point 2: This will render value and delta but both should be independent to render */}
//                 {/* <div className={`flex flex-col justify-end gap-3 mt-auto`}>
//                     <h3 className="mb-0 font-semibold text-2xl">$400</h3>
//                     <div className={`inline-flex flex-nowrap gap-1 items-center rounded-md py-[6px] px-2 bg-green-100`}>
//                         <span className={`text-base font-medium text-green-600 text-nowrap`}>+ 10%</span>
//                         <img src={ChartUpIcon} alt="" className="h-3 w-3" />
//                     </div>
//                 </div> */}

//                 <div className={`relative mt-2 flex-shrink ${(props?.sideMeta || safeSection?.sideMeta) ? 'w-1/2' : 'w-full'}`} aria-busy={loading ? 'true' : 'false'}>
//                     <div className="min-h-[280px]">
//                         {type !== 'bar-chart' ? (
//                             <Chart
//                                 rows={displayRows}
//                                 xKey={effectiveXKey}
//                                 yAxisKeys={Array.isArray(effectiveYKeys) ? effectiveYKeys : []}
//                                 theme={enhancedTheme}
//                                 dataContext={{ apiRow, rows: displayRows, normalized, plotConfig }}
//                                 onExposeLegend={handleExposeLegend}
//                                 onDataPointClick={handleDataPointClick}
//                             />
//                         ) : (
//                             <Chart
//                                 data={chartData}
//                                 theme={enhancedTheme}
//                                 dataContext={{ apiRow, rows: displayRows, normalized, plotConfig }}
//                                 onExposeLegend={handleExposeLegend}
//                                 onDataPointClick={handleDataPointClick}
//                             />
//                         )}
//                     </div>
//                     {loading ? (
//                         <div className="absolute inset-0 flex items-center justify-center bg-white/70">
//                             <svg className="h-6 w-6 animate-spin text-gray-600" viewBox="0 0 24 24" fill="none">
//                                 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                                 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
//                             </svg>
//                         </div>
//                     ) : null}
//                 </div>
//             </div>


//         </div>
//     );
// }